import { CopyOutlined, DownOutlined, LoadingOutlined, UpOutlined } from '@ant-design/icons';
import { useChatContext } from '@copilotkit/react-ui';
import { message as antdMessage } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import SuccessIcon from './icons/success.react.svg?react';

const formatJson = (str: string) => {
  try {
    return JSON.stringify(JSON.parse(str));
  } catch (e) {
    return str;
  }
};

// Tool 消息组件
export const ToolMessage: React.FC<any> = ({ message, isCurrentMessage, inProgress }) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(!!message.isStreaming);
  const scrollRef = useRef<HTMLDivElement>(null);

  // 判断是否正在调用中（当前消息且正在处理，但还没有结果）
  const isLoading = isCurrentMessage && inProgress && !message.toolResult;
  const { icons } = useChatContext();
  const LoadingIcon = () => <span>{icons.activityIcon}</span>;
  // 判断是否已完成
  const isComplete = !!message.toolResult;

  // 获取工具结果内容
  const toolResult = formatJson(message.toolResult || '');
  const hasResult = toolResult && toolResult.trim().length > 0;

  useEffect(() => {
    if (message.isStreaming) {
      setIsExpanded(true);
    }
  }, [message.isStreaming]);

  useEffect(() => {
    if (isExpanded && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [isExpanded, toolResult]);

  // 复制功能
  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (hasResult) {
      try {
        await navigator.clipboard.writeText(toolResult);
        antdMessage.success(t('复制成功'));
        // 可以添加复制成功的提示
      } catch (err) {
        console.error(t('复制失败:'), err);
      }
    }
  };

  // 展开/折叠功能
  const handleToggle = () => {
    if (hasResult) {
      setIsExpanded(!isExpanded);
    }
  };

  // 检查是否是合并后的 tool 消息
  if (message.isMergedTool) {
    return (
      <>
        <div className="copilotKitMessage copilotKitToolMessage" data-message-role="tool"
          style={{
            border: '1px solid var(--grey-blue-3, #DCE3EE)',
            borderRadius: 6,
          }}
        >
          {/* Header 区域 */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '8px 12px',
              backgroundColor: '#F7F8FA',
              fontSize: '12px',
              fontWeight: '500',
              color: '#1e304a',
              height: '32px',
              boxSizing: 'border-box',
              cursor: hasResult ? 'pointer' : 'default',
              borderRadius: 6,
            }}
            onClick={handleToggle}
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span>{t('工具调用：')}{message.toolName}</span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', borderRadius: '0 0 6px 6px' }}>
              {/* 状态显示 */}
              {isLoading && (
                <span style={{ color: '#1890ff', fontSize: '14px', display: 'flex', alignItems: 'center' }}><LoadingOutlined style={{ fontSize: 14 }} /></span>
              )}
              {!isLoading && isComplete && (
                <span style={{ color: '#52c41a', fontSize: '14px', display: 'flex', alignItems: 'center' }}><SuccessIcon /></span>
              )}

              {/* 复制按钮 */}
              {(!isLoading && isComplete) && (
                <span onClick={handleCopy} style={{ display: 'flex', alignItems: 'center' }}><CopyOutlined style={{ fontSize: 14 }} /></span>
              )}

              {/* 展开/折叠按钮 */}
              {(!isLoading && isComplete) && (
                <span style={{ display: 'flex', alignItems: 'center' }} onClick={handleToggle}>
                  {isExpanded ? <UpOutlined style={{ fontSize: 14 }} /> : <DownOutlined style={{ fontSize: 12 }} />}
                </span>
              )}
            </div>
          </div>

          {/* Content 区域 - 只有在有结果且展开时才显示 */}
          {hasResult && isExpanded && (
            <div
              ref={scrollRef}
              style={{
                backgroundColor: '#DCE3EE',
                maxHeight: '150px',
                overflowY: 'auto',
              }}
            >
              <SyntaxHighlighter
                language="json"
                style={oneLight}
                customStyle={{
                  margin: 0,
                  padding: '12px',
                  fontSize: '12px',
                  background: '#fff',
                  lineHeight: '1.4',
                  borderRadius: 0,
                }}
                wrapLongLines={true}
              >
                {toolResult}
              </SyntaxHighlighter>
            </div>
          )}
        </div>

        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.4; }
          }
        `}</style>
        {(inProgress && isCurrentMessage) && <LoadingIcon />}
      </>
    );
  }

  // 未合并的 tool 消息（降级处理）
  return (
    <div className="copilotKitMessage copilotKitToolMessage" data-message-role="tool">
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '8px 12px',
          backgroundColor: '#e6f4ff',
          borderRadius: '4px',
          fontSize: '12px',
          fontWeight: '500',
          color: '#1e304a',
          borderLeft: '3px solid #1890ff',
        }}
      >
        <span
          style={{
            display: 'inline-block',
            width: '6px',
            height: '6px',
            backgroundColor: '#1890ff',
            borderRadius: '50%',
            marginRight: '8px',
          }}
        />
        <span>{message.toolName}: {message.content}</span>
      </div>
    </div>
  );
};
